import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Plus, Search, Edit2, Trash2, Package, X, ChevronDown, ChevronUp, Upload, Download, Link } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ComboProductsPage() {
  const [combos, setCombos] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredCombos, setFilteredCombos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isComponentLookupOpen, setIsComponentLookupOpen] = useState(false);
  const [editingCombo, setEditingCombo] = useState(null);
  const [expandedCombo, setExpandedCombo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [componentLookupSku, setComponentLookupSku] = useState('');
  const [componentLookupResult, setComponentLookupResult] = useState(null);
  const [componentLookupLoading, setComponentLookupLoading] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();
  const { user } = useAuth();

  // Form state
  const [comboSku, setComboSku] = useState('');
  const [comboName, setComboName] = useState('');
  const [components, setComponents] = useState([{ component_sku: '', quantity: 1 }]);
  const [skuSearch, setSkuSearch] = useState('');
  const [activeComponentIndex, setActiveComponentIndex] = useState(null);

  useEffect(() => {
    fetchCombos();
    fetchProducts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchTerm) {
      const filtered = combos.filter((c) =>
        c.combo_sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.combo_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCombos(filtered);
    } else {
      setFilteredCombos(combos);
    }
  }, [searchTerm, combos]);

  const fetchCombos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/combos`);
      setCombos(response.data);
      setFilteredCombos(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load combo products',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      // Filter out combo products from the list (you can't add a combo as component of another combo)
      const singleProducts = response.data.filter(p => !p.is_combo);
      setProducts(singleProducts);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!comboSku.trim()) {
      toast({ title: 'Error', description: 'Combo SKU is required', variant: 'destructive' });
      return;
    }
    if (!comboName.trim()) {
      toast({ title: 'Error', description: 'Combo Name is required', variant: 'destructive' });
      return;
    }
    
    const validComponents = components.filter(c => c.component_sku && c.quantity > 0);
    if (validComponents.length === 0) {
      toast({ title: 'Error', description: 'At least one component is required', variant: 'destructive' });
      return;
    }
    
    if (validComponents.length > 5) {
      toast({ title: 'Error', description: 'Maximum 5 components allowed per combo', variant: 'destructive' });
      return;
    }

    try {
      const payload = {
        combo_sku: comboSku.trim(),
        combo_name: comboName.trim(),
        components: validComponents
      };

      if (editingCombo) {
        await axios.put(`${API}/combos/${editingCombo.id}`, payload);
        toast({ title: 'Success', description: 'Combo updated successfully' });
      } else {
        await axios.post(`${API}/combos`, payload);
        toast({ title: 'Success', description: 'Combo created successfully' });
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchCombos();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save combo',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this combo?')) return;
    
    try {
      await axios.delete(`${API}/combos/${id}`);
      toast({ title: 'Success', description: 'Combo deleted successfully' });
      fetchCombos();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete combo',
        variant: 'destructive',
      });
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/combos/bulk-import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadResult(response.data);
      toast({
        title: 'Import Complete',
        description: `${response.data.created} created, ${response.data.updated} updated`,
      });
      fetchCombos();
    } catch (error) {
      toast({
        title: 'Import Failed',
        description: error.response?.data?.detail || 'Failed to import combos',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/combos/download-template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'combo_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download template', variant: 'destructive' });
    }
  };

  const handleComponentLookup = async () => {
    if (!componentLookupSku.trim()) {
      toast({ title: 'Error', description: 'Please enter a SKU to search', variant: 'destructive' });
      return;
    }
    
    setComponentLookupLoading(true);
    setComponentLookupResult(null);
    
    try {
      const response = await axios.get(`${API}/combos/component-of/${encodeURIComponent(componentLookupSku.trim())}`);
      setComponentLookupResult(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to lookup component',
        variant: 'destructive',
      });
    } finally {
      setComponentLookupLoading(false);
    }
  };

  const resetForm = () => {
    setComboSku('');
    setComboName('');
    setComponents([{ component_sku: '', quantity: 1 }]);
    setEditingCombo(null);
    setSkuSearch('');
    setActiveComponentIndex(null);
  };

  const openDialog = (combo = null) => {
    if (combo) {
      setEditingCombo(combo);
      setComboSku(combo.combo_sku);
      setComboName(combo.combo_name);
      setComponents(combo.components.map(c => ({
        component_sku: c.component_sku,
        quantity: c.quantity
      })));
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const addComponent = () => {
    if (components.length >= 5) {
      toast({ title: 'Limit Reached', description: 'Maximum 5 components allowed', variant: 'destructive' });
      return;
    }
    setComponents([...components, { component_sku: '', quantity: 1 }]);
  };

  const removeComponent = (index) => {
    if (components.length <= 1) return;
    setComponents(components.filter((_, i) => i !== index));
  };

  const updateComponent = (index, field, value) => {
    const updated = [...components];
    updated[index] = { ...updated[index], [field]: value };
    setComponents(updated);
  };

  const selectProduct = (index, sku) => {
    updateComponent(index, 'component_sku', sku);
    setActiveComponentIndex(null);
    setSkuSearch('');
  };

  const getFilteredProducts = () => {
    if (!skuSearch) return products.slice(0, 10);
    return products.filter(p => 
      p.sku.toLowerCase().includes(skuSearch.toLowerCase()) ||
      p.name.toLowerCase().includes(skuSearch.toLowerCase())
    ).slice(0, 10);
  };

  const toggleExpanded = (comboId) => {
    setExpandedCombo(expandedCombo === comboId ? null : comboId);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight font-heading" data-testid="page-title">
              Combo Products
            </h1>
            <p className="text-muted-foreground mt-2">
              {combos.length} combo{combos.length !== 1 ? 's' : ''} defined
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsComponentLookupOpen(true)} data-testid="component-lookup-button">
              <Link className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Component Lookup
            </Button>
            <Button variant="outline" onClick={() => setIsBulkUploadOpen(true)} data-testid="bulk-import-button">
              <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Bulk Import
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button onClick={() => openDialog()} data-testid="create-combo-button">
                  <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Create Combo
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle data-testid="dialog-title">
                    {editingCombo ? 'Edit Combo' : 'Create New Combo'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="combo-form">
                  {/* Combo Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Combo SKU *</label>
                      <Input
                        data-testid="input-combo-sku"
                        value={comboSku}
                        onChange={(e) => setComboSku(e.target.value)}
                        placeholder="e.g., COMBO-001"
                        required
                        disabled={!!editingCombo}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Combo Name *</label>
                      <Input
                        data-testid="input-combo-name"
                        value={comboName}
                        onChange={(e) => setComboName(e.target.value)}
                        placeholder="e.g., Summer Bundle Pack"
                        required
                      />
                    </div>
                  </div>

                  {/* Components Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">
                        Components ({components.length}/5)
                      </label>
                      <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addComponent}
                      disabled={components.length >= 5}
                      data-testid="add-component-button"
                    >
                      <Plus className="w-3 h-3 mr-1" strokeWidth={1.5} />
                      Add Component
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {components.map((comp, index) => (
                      <div 
                        key={`comp-${comp.component_sku || index}`} 
                        className="bg-muted/30 border border-border rounded-sm p-4"
                        data-testid={`component-${index}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-1 space-y-3">
                            <div className="relative">
                              <label className="block text-xs text-muted-foreground mb-1">
                                Product SKU
                              </label>
                              <Input
                                data-testid={`input-component-sku-${index}`}
                                value={comp.component_sku}
                                onChange={(e) => {
                                  updateComponent(index, 'component_sku', e.target.value);
                                  setSkuSearch(e.target.value);
                                  setActiveComponentIndex(index);
                                }}
                                onFocus={() => setActiveComponentIndex(index)}
                                placeholder="Search or enter SKU..."
                                className="font-mono"
                              />
                              
                              {/* SKU Dropdown */}
                              {activeComponentIndex === index && (
                                <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-sm shadow-lg max-h-48 overflow-y-auto">
                                  {getFilteredProducts().map((product) => (
                                    <button
                                      key={product.sku}
                                      type="button"
                                      onClick={() => selectProduct(index, product.sku)}
                                      className="w-full px-3 py-2 text-left hover:bg-muted/50 flex items-center justify-between border-b border-border last:border-b-0"
                                      data-testid={`product-option-${product.sku}`}
                                    >
                                      <div>
                                        <span className="font-mono text-sm">{product.sku}</span>
                                        <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                          {product.name}
                                        </p>
                                      </div>
                                      <span className="text-xs text-muted-foreground">
                                        Stock: {product.inventory}
                                      </span>
                                    </button>
                                  ))}
                                  {getFilteredProducts().length === 0 && (
                                    <div className="px-3 py-2 text-sm text-muted-foreground">
                                      No products found
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            <div className="w-32">
                              <label className="block text-xs text-muted-foreground mb-1">
                                Quantity
                              </label>
                              <Input
                                data-testid={`input-component-qty-${index}`}
                                type="number"
                                min="1"
                                value={comp.quantity}
                                onChange={(e) => updateComponent(index, 'quantity', parseInt(e.target.value) || 1)}
                              />
                            </div>
                          </div>
                          
                          {components.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeComponent(index)}
                              className="p-1 hover:bg-destructive/10 text-destructive rounded-sm transition-colors mt-6"
                              data-testid={`remove-component-${index}`}
                            >
                              <X className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-4">
                  <p className="text-sm text-blue-600">
                    <strong>Note:</strong> Combos track bundled products. For FBA/Noon warehouses, 
                    stock is maintained as combo units. For the offline store (central warehouse), 
                    components are stored individually.
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="submit" data-testid="save-combo-button">
                    {editingCombo ? 'Update Combo' : 'Create Combo'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsDialogOpen(false);
                      resetForm();
                    }}
                    data-testid="cancel-button"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

        {/* Search */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <Input
              data-testid="search-input"
              placeholder="Search combos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Combos List */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredCombos.length > 0 ? (
            filteredCombos.map((combo) => (
              <div
                key={combo.id}
                className="border border-border rounded-sm bg-background"
                data-testid={`combo-card-${combo.combo_sku}`}
              >
                {/* Combo Header */}
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => toggleExpanded(combo.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                      <Package className="w-5 h-5 text-primary" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium">{combo.combo_sku}</span>
                        <span className="text-xs bg-muted px-2 py-0.5 rounded">
                          {combo.components.length} component{combo.components.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{combo.combo_name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Max Assemblable</p>
                      <p className="font-mono font-medium">{combo.max_assemblable}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDialog(combo);
                        }}
                        className="p-2 hover:bg-muted rounded-sm transition-colors"
                        data-testid={`edit-${combo.combo_sku}`}
                      >
                        <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      {user?.role === 'admin' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(combo.id);
                          }}
                          className="p-2 hover:bg-destructive/10 text-destructive rounded-sm transition-colors"
                          data-testid={`delete-${combo.combo_sku}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      )}
                      {expandedCombo === combo.id ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Components */}
                {expandedCombo === combo.id && (
                  <div className="border-t border-border bg-muted/20 p-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Components</p>
                    <div className="grid gap-2">
                      {combo.components.map((comp, idx) => (
                        <div 
                          key={`${comp.component_sku}-${idx}`}
                          className="flex items-center justify-between bg-background border border-border rounded-sm px-4 py-3"
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 bg-muted rounded-sm flex items-center justify-center text-xs font-medium">
                              {idx + 1}
                            </span>
                            <div>
                              <span className="font-mono text-sm">{comp.component_sku}</span>
                              <p className="text-xs text-muted-foreground">{comp.component_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">Qty per Combo</p>
                              <p className="font-mono font-medium">{comp.quantity}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">Available Stock</p>
                              <p className={`font-mono font-medium ${comp.available_stock < 50 ? 'text-destructive' : ''}`}>
                                {comp.available_stock}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="border border-border rounded-sm p-12 text-center">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
              <p className="text-muted-foreground" data-testid="no-combos-message">
                {searchTerm ? 'No combos match your search' : 'No combos defined yet. Create your first combo to get started.'}
              </p>
            </div>
          )}
        </div>

        {/* Bulk Upload Dialog */}
        <Dialog open={isBulkUploadOpen} onOpenChange={(open) => {
          setIsBulkUploadOpen(open);
          if (!open) setUploadResult(null);
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Bulk Import Combos</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Upload a CSV or Excel file to bulk import combo products. Existing combos with the same SKU will be updated.
              </p>
              
              <div className="bg-muted/30 border border-border rounded-sm p-4">
                <p className="text-sm font-medium mb-2">Required Columns:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <strong>Combo SKU</strong> - Unique identifier for the combo</li>
                  <li>• <strong>Combo Name</strong> - Display name</li>
                  <li>• <strong>Component 1 SKU</strong>, <strong>Component 1 Qty</strong></li>
                  <li>• <strong>Component 2 SKU</strong>, <strong>Component 2 Qty</strong> (optional)</li>
                  <li>• ... up to Component 5</li>
                </ul>
              </div>

              <Button variant="outline" onClick={downloadTemplate} className="w-full" data-testid="download-combo-template">
                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Download Template
              </Button>

              <div>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleBulkUpload}
                  disabled={uploading}
                  data-testid="combo-upload-input"
                />
              </div>

              {uploading && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  <span className="ml-2 text-sm">Importing combos...</span>
                </div>
              )}

              {uploadResult && (
                <div className="bg-muted/30 border border-border rounded-sm p-4 space-y-2">
                  <p className="font-medium">Import Results:</p>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Created</p>
                      <p className="font-mono text-lg text-green-600">{uploadResult.created}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Updated</p>
                      <p className="font-mono text-lg text-blue-600">{uploadResult.updated}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Skipped</p>
                      <p className="font-mono text-lg text-orange-600">{uploadResult.skipped}</p>
                    </div>
                  </div>
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-sm font-medium text-destructive mb-1">Errors:</p>
                      <ul className="text-xs text-muted-foreground space-y-1 max-h-24 overflow-y-auto">
                        {uploadResult.errors.map((err, idx) => (
                          <li key={`err-${idx}`}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Component Lookup Dialog */}
        <Dialog open={isComponentLookupOpen} onOpenChange={(open) => {
          setIsComponentLookupOpen(open);
          if (!open) {
            setComponentLookupSku('');
            setComponentLookupResult(null);
          }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Component Lookup</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Search for a component SKU to see all the combos it is part of.
              </p>
              
              <div className="flex gap-2">
                <Input
                  data-testid="component-lookup-input"
                  placeholder="Enter component SKU..."
                  value={componentLookupSku}
                  onChange={(e) => setComponentLookupSku(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleComponentLookup()}
                  className="font-mono"
                />
                <Button 
                  onClick={handleComponentLookup} 
                  disabled={componentLookupLoading}
                  data-testid="component-lookup-search-btn"
                >
                  {componentLookupLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Search className="w-4 h-4" strokeWidth={1.5} />
                  )}
                </Button>
              </div>

              {componentLookupResult && (
                <div className="space-y-4">
                  <div className="bg-muted/30 border border-border rounded-sm p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Component SKU</p>
                        <p className="font-mono font-medium">{componentLookupResult.component_sku}</p>
                        {componentLookupResult.component_name && (
                          <p className="text-sm text-muted-foreground">{componentLookupResult.component_name}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Used in</p>
                        <p className="text-2xl font-bold">{componentLookupResult.total_count}</p>
                        <p className="text-xs text-muted-foreground">combo{componentLookupResult.total_count !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </div>

                  {componentLookupResult.combos.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {componentLookupResult.combos.map((combo) => (
                        <div 
                          key={combo.combo_id}
                          className="flex items-center justify-between bg-background border border-border rounded-sm px-4 py-3"
                          data-testid={`lookup-result-${combo.combo_sku}`}
                        >
                          <div>
                            <span className="font-mono text-sm">{combo.combo_sku}</span>
                            <p className="text-xs text-muted-foreground">{combo.combo_name}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">Qty in Combo</p>
                              <p className="font-mono font-medium">{combo.quantity_in_combo}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">Components</p>
                              <p className="font-mono text-sm">{combo.total_components}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 border border-border rounded-sm bg-muted/20">
                      <Package className="w-8 h-8 text-muted-foreground mx-auto mb-2" strokeWidth={1} />
                      <p className="text-sm text-muted-foreground">
                        This component is not used in any combo
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
